import type { ActivePalette } from "../../utils/activePalette/types";
import type { BlockVisibility } from "../common/BlockVisibility";
import type { Unit } from "../../components/UnitsSelector/UnitsSelector";
import type { UnitGroupConfig, UnitGroupingMode } from "@autodromo/units";
import type { useGlobalQueryParams } from "../../hooks/queryParams/useGlobalQueryParams";
import type { PageEntry } from "../../utils/pageEntry";
import type { AntiBotMeta } from "../../constants/antiBot";
import type { WhatsappItem } from "../Header/Header";
import type { ImageProps } from "../../utils/image/types";
import type { DynamicStructure } from "../../utils/interpolate";
import type { MegaMenuStatus } from "../../utils/megaMenuSource";

export enum LogoRedirectType {
  HOMEPAGE = "homepage",
  URL = "url",
  PAGE = "page",
}

export interface LogoRedirectConfig {
  enabled: boolean;
  type: LogoRedirectType;
  url?: string;
  pageSlug?: string;
  openInNewTab?: boolean;
}

export enum MenuRedirectType {
  PAGE = "page",
  EXTERNAL_URL = "external_url",
  SCROLL_TO_BLOCK = "scroll_to_block",
}

export interface MenuRedirectConfig {
  type: MenuRedirectType;
  pageSlug?: string;
  url?: string;
  openInNewTab?: boolean;
  scrollPageSlug?: string;
  blockId?: string;
}

export enum HeaderV2Layout {
  BASIC = "basic",
  EXTENDED = "extended",
  EXTENDED_TOP = "extended-top",
}

export enum HeaderScrollState {
  TOP = "top",
  SCROLLED = "scrolled",
}

export enum HeaderSlot {
  LOGO = "logo",
  MENU = "menu",
  ACTIONS = "actions",
  BUTTONS = "buttons",
  UNITS = "units",
}

export enum BlurStrength {
  SM = "sm",
  MD = "md",
  LG = "lg",
}

export enum MobileMenuMode {
  DRAWER = "drawer",
  FULLSCREEN = "fullscreen",
}

export enum MobileDrawerSide {
  LEFT = "left",
  RIGHT = "right",
}

export enum BurgerPosition {
  START = "start",
  END = "end",
}

export enum BurgerStyle {
  LINES = "lines",
  SHORT_LAST = "short-last",
  SPACED = "spaced",
}

export enum SubmenuStyle {
  ATTACHED = "attached",
  FLOATING = "floating",
}

export enum MenuSpacing {
  COMPACT = "compact",
  SPACIOUS = "spacious",
}

export enum MenuAlign {
  START = "start",
  CENTER = "center",
}

export enum MobileDividers {
  LINES = "lines",
  NONE = "none",
}

export enum BarWidth {
  WIDE = "wide",
  COMPACT = "compact",
}

export enum MegaMenuDensity {
  COMFORTABLE = "comfortable",
  COMPACT = "compact",
}

export enum ButtonVariant {
  FILLED = "filled",
  OUTLINE = "outline",
  LINK = "link",
}

export enum ButtonMobileDisplay {
  BAR = "bar",
  DRAWER = "drawer",
  HIDDEN = "hidden",
}

export enum UnitsSelectorStyle {
  TEXT = "text",
  ROUNDED = "rounded",
  ROUND = "round",
}

export enum UnitsMobilePlacement {
  BAR = "bar",
  DRAWER = "drawer",
}

export enum UnitsMobileAlign {
  START = "start",
  CENTER = "center",
  END = "end",
}

export enum IconAlignment {
  LEFT = "left",
  RIGHT = "right",
}

export enum BarVariant {
  TOP = "top",
  BOTTOM = "bottom",
}

export enum ContactKind {
  WHATSAPP = "whatsapp",
  PHONE = "phone",
}

export type ContactBadgeRadius = string;

export interface ContactButtonConfig {
  icon?: string;
  mobileDisplay?: ButtonMobileDisplay;
}

export interface UnitsSelectorConfig {
  style?: UnitsSelectorStyle;
  icon?: string;
  showIcon?: boolean;
  iconPosition?: IconAlignment;
  divider?: boolean;
  mobilePlacement?: UnitsMobilePlacement;
  mobileAlign?: UnitsMobileAlign;
  mobileDivider?: boolean;
}

export enum MegaMenuWidth {
  CONTENT = "content",
  FULL = "full",
}

export enum MegaMenuHeight {
  AUTO = "auto",
  FULLSCREEN = "fullscreen",
}

export enum MegaMenuAspect {
  FIXED = "fixed",
  AUTO = "auto",
}

export enum MegaMenuAlign {
  START = "start",
  CENTER = "center",
  END = "end",
}

export enum MegaMenuTrigger {
  HOVER = "hover",
  CLICK = "click",
}

export enum MegaMenuCardLayout {
  ABOVE = "above",
  BESIDE = "beside",
}

export enum MegaMenuCardAlign {
  START = "start",
  CENTER = "center",
}

export interface MegaMenuFieldMap {
  image?: string;
  title?: string;
  description?: string;
  tag?: string;
  showImage?: boolean;
  showTitle?: boolean;
  showDescription?: boolean;
  showTag?: boolean;
}

export interface MegaMenuItemConfig {
  enabled: boolean;
  contentTypeId?: string;
  moduleSlug?: string;
  fields?: MegaMenuFieldMap;
  showSeeAll?: boolean;
  seeAllLabel?: string;
  seeAllRedirect?: MenuRedirectConfig;
  showCardCta?: boolean;
  cardCtaLabel?: string;
  cardCtaVariant?: ButtonVariant;
  cardCtaShowIcon?: boolean;
  cardCtaIcon?: string;
  cardCtaIconAlignment?: IconAlignment;
}

export interface HeaderV2Link {
  id?: number | string;
  name: string;
  isActive?: boolean;
  enableLinks?: boolean;
  redirect?: MenuRedirectConfig;
  icon?: string;
  description?: string;
  link?: string;
  newTab?: boolean;
  children?: HeaderV2Link[];
  submenuColumns?: number;
  megaMenu?: MegaMenuItemConfig;
}

export interface HeaderV2Button {
  id?: number | string;
  isActive: boolean;
  text?: string;
  textColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  icon?: string;
  showIcon?: boolean;
  redirect?: MenuRedirectConfig;
  link?: string;
  openInNewTab?: boolean;
  buttonIconAlignment?: IconAlignment;
  variant?: ButtonVariant;
  mobileDisplay?: ButtonMobileDisplay;
}

export interface HeaderStateColors {
  background?: string;
  border?: string;
  links?: string;
  linksHover?: string;
  submenu?: string;
  submenuText?: string;
  submenuBorder?: string;
  submenuDescription?: string;
  submenuIcon?: string;
  icons?: string;
  buttonsText?: string;
  buttonsBackground?: string;
  buttonsBorder?: string;
  whatsappBackground?: string;
  whatsappIcon?: string;
  whatsappBorder?: string;
  phoneBackground?: string;
  phoneIcon?: string;
  phoneBorder?: string;
  outlineText?: string;
  outlineBorder?: string;
  linkText?: string;
  unitsText?: string;
  unitsBackground?: string;
  unitsBorder?: string;
  unitsDivider?: string;
  topBarBackground?: string;
  topBarBorder?: string;
}

export interface HeaderV2Colors {
  top: HeaderStateColors;
  scrolled: HeaderStateColors;
}

export interface MegaMenuConfig {
  columns: number;
  aspect: MegaMenuAspect;
  width: MegaMenuWidth;
  align?: MegaMenuAlign;
  trigger?: MegaMenuTrigger;
  cardLayout?: MegaMenuCardLayout;
  density?: MegaMenuDensity;
  height?: MegaMenuHeight;
  maxItems?: number;
  cardAlign?: MegaMenuCardAlign;
  panelBackground?: string;
  panelBorder?: string;
  thumbBackground?: string;
  thumbTitle?: string;
  thumbDescription?: string;
  thumbTag?: string;
  cardCtaColor?: string;
  cardCtaBackground?: string;
  seeAllColor?: string;
}

export type MegaMenuEntry = Record<string, unknown>;

export interface MegaMenuLinkData {
  status: MegaMenuStatus;
  entries: MegaMenuEntry[];
}

export type MegaMenuData = Record<string, MegaMenuLinkData>;

export interface MegaMenuCard {
  id: string;
  label: string;
  title?: string;
  image?: { src: string; alt?: string };
  description?: string;
  tag?: string;
  url: string;
}

export type PaddingProps = {
  typeMeasure?: string;
  desktop?: { paddingTop?: number; paddingBottom?: number };
  mobile?: { paddingTop?: number; paddingBottom?: number };
};

export interface HeaderV2ImageConfig<T> {
  render: (props: T) => JSX.Element;
  props: T;
  redirect?: LogoRedirectConfig;
}

export interface HeaderV2Props<T = ImageProps>
  extends React.HTMLAttributes<HTMLDivElement>,
    ActivePalette,
    BlockVisibility {
  htmlId?: string;
  preload?: boolean;
  padding?: PaddingProps;

  image: HeaderV2ImageConfig<T>;
  image2?: HeaderV2ImageConfig<T>;
  showImage2?: boolean;
  imageScrolled?: HeaderV2ImageConfig<T>;
  image2Scrolled?: HeaderV2ImageConfig<T>;
  showImageScrolled?: boolean;

  layout?: HeaderV2Layout;
  barWidth?: BarWidth;
  links: HeaderV2Link[];
  buttons: HeaderV2Button[];

  colors?: HeaderV2Colors;
  blurEnabled?: boolean;
  blurStrength?: BlurStrength;

  fixed?: boolean;
  overlapFirstBlock?: boolean;
  hideOnScrollDown?: boolean;
  scrollThreshold?: number;

  megaMenu?: MegaMenuConfig;
  megaMenuData?: MegaMenuData;
  dynamicStructure?: DynamicStructure;

  mobileMenuMode?: MobileMenuMode;
  mobileDrawerSide?: MobileDrawerSide;
  burgerPosition?: BurgerPosition;
  burgerStyle?: BurgerStyle;
  mobileDividers?: MobileDividers;
  menuAlign?: MenuAlign;
  menuSpacing?: MenuSpacing;
  submenuStyle?: SubmenuStyle;
  submenuRadius?: string;
  contactRadius?: ContactBadgeRadius;
  drawerBackground?: string;
  drawerText?: string;
  mobileBackdrop?: boolean;

  isBuilder?: boolean;
  forceScrollState?: HeaderScrollState;
  onMenuFitChange?: (fits: boolean) => void;

  units?: Unit[];
  showUnitsSelector?: boolean;
  unitsSelector?: UnitsSelectorConfig;
  showAllUnitsOption?: boolean;
  allUnitsLabel?: string;
  unitGroupingMode?: UnitGroupingMode | null;
  unitGroups?: UnitGroupConfig[] | null;
  unitGroupingLabel?: string | null;
  useGlobalQueryParams?: typeof useGlobalQueryParams;
  showPhoneButton?: boolean;
  phoneButton?: ContactButtonConfig;
  showHeaderWhatsappButton?: boolean;
  whatsappButton?: ContactButtonConfig;
  hasWhatsappItems?: boolean;
  whatsappItems?: WhatsappItem[];
  whatsappButtonColor?: string;
  whatsappSubmitUrl?: string;
  pageEntry?: PageEntry | null;
  antiBot: AntiBotMeta | null;
  accountId?: string;
  siteId?: string;
  product?: string;

  className?: string;
}

export enum SectionPosition {
  RELATIVE = "relative",
  STICKY = "sticky",
  ABSOLUTE = "absolute",
}

export enum OverlapMode {
  NONE = "none",
  COLLAPSE = "collapse",
  MARGIN = "margin",
}

export type SectionPlacement = { position: SectionPosition; overlap: OverlapMode };

export enum MenuItemMode {
  LINK = "link",
  SUBMENU = "submenu",
  MEGAMENU = "megamenu",
}
